// View 3 — User detail / edit drawer.
//
// Per design/UI : UX v1.0/for-engineering-users/specs/users-strings.md.
// Sections (top → bottom):
//   - Header: avatar + name + email + status pill
//   - Meta grid: ROLE / REGION / MFA / TENURE / LAST SEEN / CREATED BY
//   - Change role select (Phase 0: single role per user)
//   - Permissions chips (read-only — driven by selected role)
//   - Active sessions row + "Sign out from all devices"
//   - Actions: Send password reset · Reset MFA device · Resend invite (if invited)
//   - Danger footer: Suspend access · Remove user (with confirm step)

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Drawer,
  RoleBadge,
  Spinner,
} from '@gc-coach/design-system';
import {
  ALL_ROLES,
  permissionsForRoles,
  type RoleKey,
  type UserDetail,
} from '@gc-coach/shared';
import {
  deleteUser,
  getUser,
  patchUser,
  reactivateUser,
  resendInvite,
  resetMfa,
  restoreUser,
  sendPasswordReset,
  signOutAll,
  suspendUser,
} from '../api/endpoints.js';
import { useSession } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import { roleLabel } from '../shell/roleLabels.js';
import { formatRelativeTime } from './time.js';
import styles from './UserDetailDrawer.module.css';

export interface UserDetailDrawerProps {
  userId: string;
  onClose: () => void;
  /** Called after a patch / suspend / reactivate / resend etc. (re-render table). */
  onChanged: () => void;
  /** Called after a hard remove — table should drop the row + close drawer. */
  onRemoved: () => void;
}

type ConfirmKind = null | 'suspend' | 'remove' | 'restore';

export function UserDetailDrawer({
  userId,
  onClose,
  onChanged,
  onRemoved,
}: UserDetailDrawerProps) {
  const { t, tt } = useI18n();
  const { session } = useSession();
  const isSuperAdmin = session?.roles.includes('super_admin') ?? false;

  const [user, setUser] = useState<UserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionPending, setActionPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  // Removal optional reason — captured in the confirm modal.
  const [removeReason, setRemoveReason] = useState('');
  // Type-to-confirm value for the restore-removed-user flow.
  const [restoreConfirmText, setRestoreConfirmText] = useState('');

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const result = await getUser(userId);
      if (cancelled) return;
      if (result.ok) {
        setUser(result.data.user);
      } else {
        setError(t('users.detailGenericError'));
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, t]);

  const grantedPerms = useMemo(
    () => (user ? permissionsForRoles(user.roles) : []),
    [user],
  );

  // ─── Action wrappers ───────────────────────────────────────────────
  async function runWithUpdate(
    op: () => Promise<{ ok: true; data: { user: UserDetail } } | { ok: false; code: string }>,
  ) {
    setActionPending(true);
    setError(null);
    const result = await op();
    setActionPending(false);
    if (result.ok) {
      setUser(result.data.user);
      onChanged();
    } else {
      setError(t('users.detailGenericError'));
    }
  }

  async function onChangeRole(next: RoleKey) {
    if (!user) return;
    if (user.roles.length === 1 && user.roles[0] === next) return;
    await runWithUpdate(() => patchUser(user.id, { roles: [next] }));
  }

  async function onResendInvite() {
    if (!user) return;
    await runWithUpdate(() => resendInvite(user.id));
  }

  async function onResetMfa() {
    if (!user) return;
    await runWithUpdate(() => resetMfa(user.id));
  }

  async function onSendPasswordReset() {
    if (!user) return;
    setActionPending(true);
    setError(null);
    const result = await sendPasswordReset(user.id);
    setActionPending(false);
    if (!result.ok) setError(t('users.detailGenericError'));
  }

  async function onSignOutAll() {
    if (!user) return;
    setActionPending(true);
    setError(null);
    const result = await signOutAll(user.id);
    setActionPending(false);
    if (!result.ok) setError(t('users.detailGenericError'));
  }

  async function onSuspendConfirmed() {
    if (!user) return;
    setConfirm(null);
    await runWithUpdate(() => suspendUser(user.id));
  }

  async function onReactivate() {
    if (!user) return;
    await runWithUpdate(() => reactivateUser(user.id));
  }

  async function onRemoveConfirmed() {
    if (!user) return;
    const reason = removeReason.trim() || undefined;
    setConfirm(null);
    setRemoveReason('');
    setActionPending(true);
    setError(null);
    const result = await deleteUser(user.id, reason);
    setActionPending(false);
    if (result.ok) {
      // Removed users stay in Mongo + Cognito (soft remove). The drawer
      // can close and the table should re-fetch — onRemoved() does both.
      // The table's default view hides removed users so the row will
      // disappear unless the "Removed" filter is selected.
      onRemoved();
    } else {
      setError(t('users.detailGenericError'));
    }
  }

  async function onRestoreConfirmed() {
    if (!user) return;
    if (restoreConfirmText !== 'RESTORE') {
      // Defensive — the button is disabled until matched, but in case.
      return;
    }
    setConfirm(null);
    setRestoreConfirmText('');
    setActionPending(true);
    setError(null);
    const result = await restoreUser(user.id);
    setActionPending(false);
    if (result.ok) {
      setUser(result.data.user);
      onChanged();
    } else if (result.code === 'FORBIDDEN') {
      setError(t('users.detailRestoreForbidden'));
    } else {
      setError(t('users.detailGenericError'));
    }
  }

  // ─── Render ───────────────────────────────────────────────────────

  return (
    <Drawer
      open
      onClose={actionPending ? () => undefined : onClose}
      title={user ? user.displayName : ''}
      footer={
        user && (
          <div className={styles.dangerFooter}>
            {user.status === 'removed' ? (
              // Removed user: only a Restore button (super_admin-only).
              // Non-super_admin sees it disabled with a tooltip.
              <Button
                kind="ghost"
                onClick={() => setConfirm('restore')}
                disabled={actionPending || !isSuperAdmin}
                title={
                  isSuperAdmin
                    ? undefined
                    : t('users.detailRestoreSuperAdminOnly')
                }
              >
                {t('users.detailRestore')}
              </Button>
            ) : (
              <>
                {user.status !== 'suspended' ? (
                  <Button
                    kind="ghost"
                    onClick={() => setConfirm('suspend')}
                    disabled={actionPending}
                  >
                    {t('users.detailSuspend')}
                  </Button>
                ) : (
                  <Button kind="ghost" onClick={onReactivate} disabled={actionPending}>
                    {t('users.detailReactivate')}
                  </Button>
                )}
                <Button
                  kind="danger"
                  onClick={() => setConfirm('remove')}
                  disabled={actionPending}
                >
                  {t('users.detailRemove')}
                </Button>
              </>
            )}
          </div>
        )
      }
    >
      {isLoading && (
        <div className={styles.loadingState}>
          <Spinner />
        </div>
      )}

      {!isLoading && !user && (
        <Alert severity="danger">{t('users.detailGenericError')}</Alert>
      )}

      {user && (
        <div className={styles.body}>
          {error && (
            <div className={styles.alert}>
              <Alert severity="danger">{error}</Alert>
            </div>
          )}

          {/* Header */}
          <header className={styles.header}>
            <Avatar
              name={user.displayName}
              email={user.email}
              size="lg"
              variant={user.status === 'invited' ? 'dashed' : 'filled'}
            />
            <div className={styles.headerText}>
              <span className={styles.headerName}>{user.displayName}</span>
              <span className={styles.headerEmail}>{user.email}</span>
            </div>
            <span className={statusClass(user.status, styles)}>{statusText(user.status, t)}</span>
          </header>

          {/* Meta grid */}
          <dl className={styles.metaGrid}>
            <MetaCell label={t('users.detailMetaRole')}>
              <RoleBadge
                role={user.roles[0] ?? 'read_only'}
                label={roleLabel(user.roles[0] ?? 'read_only', t)}
              />
            </MetaCell>
            <MetaCell label={t('users.detailMetaRegion')}>
              <span className={styles.mono}>{user.region.toUpperCase()}</span>
            </MetaCell>
            <MetaCell label={t('users.detailMetaMfa')}>
              <span>{renderMfa(user.mfaState, t)}</span>
            </MetaCell>
            <MetaCell label={t('users.detailMetaTenure')}>
              <span>
                {user.lastSeenAt
                  ? formatRelativeTime(user.createdAt)
                  : t('users.detailTenureNever')}
              </span>
            </MetaCell>
            <MetaCell label={t('users.detailMetaLastSeen')}>
              <span>
                {user.lastSeenAt
                  ? formatRelativeTime(user.lastSeenAt)
                  : t('users.detailLastSeenNever')}
              </span>
            </MetaCell>
            <MetaCell label={t('users.detailMetaCreatedBy')}>
              <span>{user.createdBy}</span>
            </MetaCell>
          </dl>

          {/* Change role */}
          <section className={styles.section}>
            <label className={styles.sectionLabel} htmlFor="change-role">
              {t('users.detailChangeRole')}
            </label>
            <select
              id="change-role"
              className={styles.roleSelect}
              value={user.roles[0] ?? 'read_only'}
              onChange={(e) => void onChangeRole(e.target.value as RoleKey)}
              disabled={actionPending}
            >
              {ALL_ROLES.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r, t)}
                </option>
              ))}
            </select>
          </section>

          {/* Permissions chips */}
          <section className={styles.section}>
            <span className={styles.sectionLabel}>{t('users.detailPermissions')}</span>
            <div className={styles.permChips}>
              {grantedPerms.map((perm) => (
                <span key={perm} className={styles.permChip}>
                  {t(`permissionLabels.${perm}`)}
                </span>
              ))}
            </div>
          </section>

          {/* Active sessions */}
          <section className={styles.section}>
            <span className={styles.sectionLabel}>{t('users.detailActiveSessions')}</span>
            <p className={styles.sessionsBody}>{t('users.detailSessionsBody')}</p>
            <Button kind="ghost" onClick={onSignOutAll} disabled={actionPending}>
              {t('users.detailSignOutAll')}
            </Button>
          </section>

          {/* Actions */}
          <section className={styles.actions}>
            {user.status === 'invited' && (
              <Button kind="ghost" onClick={onResendInvite} disabled={actionPending}>
                {t('users.detailResendInvite')}
              </Button>
            )}
            {/* Admin-triggered password reset. Only meaningful for
              * active users — invited users have no password to reset
              * (use Resend Invite instead), suspended/removed users
              * are disabled in Cognito and the backend returns 409. */}
            <Button
              kind="ghost"
              onClick={onSendPasswordReset}
              disabled={actionPending || user.status !== 'active'}
              title={
                user.status === 'active'
                  ? undefined
                  : t('users.detailSendResetActiveOnly')
              }
            >
              {t('users.detailSendReset')}
            </Button>
            <Button kind="ghost" onClick={onResetMfa} disabled={actionPending}>
              {t('users.detailResetMfa')}
            </Button>
          </section>

          {/* Confirm modal (inline — no separate primitive yet) */}
          {confirm === 'suspend' && (
            <ConfirmPanel
              title={t('users.detailConfirmSuspend')}
              body={t('users.detailConfirmSuspendBody')}
              confirmLabel={t('users.detailConfirmYes')}
              cancelLabel={t('users.detailConfirmCancel')}
              onConfirm={onSuspendConfirmed}
              onCancel={() => setConfirm(null)}
              tone="warn"
              data-purpose={tt('users.detailConfirmYes', {})}
            />
          )}
          {confirm === 'remove' && (
            <RemoveConfirmPanel
              title={t('users.detailConfirmRemove')}
              body={t('users.detailConfirmRemoveBody')}
              reasonLabel={t('users.detailConfirmRemoveReasonLabel')}
              reasonPlaceholder={t('users.detailConfirmRemoveReasonPlaceholder')}
              confirmLabel={t('users.detailConfirmYes')}
              cancelLabel={t('users.detailConfirmCancel')}
              reason={removeReason}
              setReason={setRemoveReason}
              onConfirm={onRemoveConfirmed}
              onCancel={() => {
                setConfirm(null);
                setRemoveReason('');
              }}
            />
          )}
          {confirm === 'restore' && (
            <RestoreConfirmPanel
              title={t('users.detailConfirmRestore')}
              body={t('users.detailConfirmRestoreBody')}
              typeToConfirmLabel={t('users.detailConfirmRestoreTypeLabel')}
              expectedToken="RESTORE"
              confirmLabel={t('users.detailConfirmRestore')}
              cancelLabel={t('users.detailConfirmCancel')}
              value={restoreConfirmText}
              setValue={setRestoreConfirmText}
              onConfirm={onRestoreConfirmed}
              onCancel={() => {
                setConfirm(null);
                setRestoreConfirmText('');
              }}
            />
          )}
        </div>
      )}
    </Drawer>
  );
}

// ─── Subcomponents ───────────────────────────────────────────────────────────

function MetaCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.metaCell}>
      <dt className={styles.metaLabel}>{label}</dt>
      <dd className={styles.metaValue}>{children}</dd>
    </div>
  );
}

function ConfirmPanel({
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  tone,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  tone: 'warn' | 'danger';
  /** Eat any extra HTML pass-through props (e.g. data-* from spreads). */
  [extra: string]: unknown;
}) {
  return (
    <div className={styles.confirm} role="dialog" aria-modal="true">
      <h3 className={styles.confirmTitle}>{title}</h3>
      <p className={styles.confirmBody}>{body}</p>
      <div className={styles.confirmButtons}>
        <Button kind="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button kind={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

function renderMfa(state: UserDetail['mfaState'], t: (k: string) => string): string {
  if (state === 'enabled') return t('users.detailMfaEnabled');
  if (state === 'pending') return t('users.detailMfaPending');
  return t('users.detailMfaNone');
}

function statusClass(s: UserDetail['status'], cls: Record<string, string>): string {
  if (s === 'active') return cls['statusActive'] ?? '';
  if (s === 'invited') return cls['statusInvited'] ?? '';
  if (s === 'removed') return cls['statusRemoved'] ?? cls['statusSuspended'] ?? '';
  return cls['statusSuspended'] ?? '';
}

function statusText(s: UserDetail['status'], t: (k: string) => string): string {
  if (s === 'active') return t('users.statusActive');
  if (s === 'invited') return t('users.statusInvited');
  if (s === 'removed') return t('users.statusRemoved');
  return t('users.statusSuspended');
}

// ─── Remove confirm panel ───────────────────────────────────────────────────
// Distinct from the generic ConfirmPanel because remove captures an optional
// reason (free text, max 500 chars server-side) for the audit log.
function RemoveConfirmPanel({
  title,
  body,
  reasonLabel,
  reasonPlaceholder,
  confirmLabel,
  cancelLabel,
  reason,
  setReason,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  confirmLabel: string;
  cancelLabel: string;
  reason: string;
  setReason: (s: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={styles.confirm} role="dialog" aria-modal="true">
      <h3 className={styles.confirmTitle}>{title}</h3>
      <p className={styles.confirmBody}>{body}</p>
      <label className={styles.confirmLabel} htmlFor="remove-reason">
        {reasonLabel}
      </label>
      <textarea
        id="remove-reason"
        className={styles.confirmTextarea}
        rows={3}
        maxLength={500}
        value={reason}
        placeholder={reasonPlaceholder}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className={styles.confirmButtons}>
        <Button kind="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button kind="danger" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

// ─── Restore confirm panel ──────────────────────────────────────────────────
// Type-to-confirm pattern: user must type RESTORE (case-sensitive) to enable
// the action. Removed users coming back is deliberately frictionful.
function RestoreConfirmPanel({
  title,
  body,
  typeToConfirmLabel,
  expectedToken,
  confirmLabel,
  cancelLabel,
  value,
  setValue,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  typeToConfirmLabel: string;
  expectedToken: string;
  confirmLabel: string;
  cancelLabel: string;
  value: string;
  setValue: (s: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const enabled = value === expectedToken;
  return (
    <div className={styles.confirm} role="dialog" aria-modal="true">
      <h3 className={styles.confirmTitle}>{title}</h3>
      <p className={styles.confirmBody}>{body}</p>
      <label className={styles.confirmLabel} htmlFor="restore-confirm">
        {typeToConfirmLabel}
      </label>
      <input
        id="restore-confirm"
        type="text"
        className={styles.confirmInput}
        value={value}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => setValue(e.target.value)}
      />
      <div className={styles.confirmButtons}>
        <Button kind="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button kind="primary" onClick={onConfirm} disabled={!enabled}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}
